import { useCallback } from 'react';
import { useAtomCallback } from 'jotai/utils';
import { useLoadingBarState } from '../../../../../components/LoadingBar/LoadingBarContext';
import { useToastMessageState } from '../../../../../components/ToastMessage/ToastMessageProvider';
import {
  bulkAssignTemplateQuery,
  deleteTemplateAssignment,
  fetchTemplatesAssignments,
  updateTemplateAssignment,
} from '../../../../../services/queries/KPITemplatesQueries';
import { getErrorMessage } from '../../../../../services/queryHelpers';
import { kpiTemplateAssignmentsListStateJ } from '../../../../../services/state/KPI/KPICompaniesStateJ';
import { LoadingId } from '../../../../../types';

import { useInvalidateKPIRequests } from '../../KPIRequests/KPIRequestResponse/hooks/useInvalidateKPIRequests';
import { TemplateAssignment } from '../../../../../schemas/template-assignment-schema';
import { prepareBulkAssignPayload } from '../AllCompaniesForms/template-assignment-utils';
import { useHandleBulkResponse } from './useHandleBulkResponse';

export interface IAssignTemplateParams extends TemplateAssignment {
  companyIds: number[];
}

export function useTemplateAssignmentActions() {
  const { actions } = useLoadingBarState();
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();
  const invalidateKpiRequests = useInvalidateKPIRequests();
  const handleBulkResponse = useHandleBulkResponse();

  const assignTemplate = useAtomCallback(
    useCallback(
      async (_get, set, payload: IAssignTemplateParams) => {
        actions.startLoading(LoadingId.bulkAssignTemplate);
        try {
          const response = await bulkAssignTemplateQuery(prepareBulkAssignPayload(payload));
          handleBulkResponse({
            response,
            onSuccess: async () => {
              await invalidateKpiRequests();
              set(kpiTemplateAssignmentsListStateJ, await fetchTemplatesAssignments());
            },
          });

          return response;
        } catch (err) {
          console.error(err);
          const message = getErrorMessage(err, `Failed to assign template`);
          pushErrorToast({ message });
        } finally {
          actions.stopLoading(LoadingId.bulkAssignTemplate);
        }
      },
      [actions, handleBulkResponse, invalidateKpiRequests, pushErrorToast]
    )
  );

  const updateAssignment = useAtomCallback(
    useCallback(
      async (_get, set, payload: Partial<TemplateAssignment>) => {
        actions.startLoading(LoadingId.updateTemplateAssignmentRequest);
        try {
          const response = await updateTemplateAssignment(payload);
          await invalidateKpiRequests();
          set(kpiTemplateAssignmentsListStateJ, await fetchTemplatesAssignments());

          pushSuccessToast({
            message: 'Successfully updated template assignment',
          });
          return response;
        } catch (err) {
          console.error(err);
          const message = getErrorMessage(err, `Failed to update template assignment`);
          pushErrorToast({ message });
        } finally {
          actions.stopLoading(LoadingId.updateTemplateAssignmentRequest);
        }
      },
      [actions, invalidateKpiRequests, pushErrorToast, pushSuccessToast]
    )
  );

  const deleteAssignment = useAtomCallback(
    useCallback(
      async (_get, set, id: number) => {
        actions.startLoading(LoadingId.deleteTemplateAssignmentRequest);

        try {
          const response = await deleteTemplateAssignment(id);
          await invalidateKpiRequests();
          set(kpiTemplateAssignmentsListStateJ, await fetchTemplatesAssignments());
          pushSuccessToast({ message: 'Template successfully removed' });
          return response;
        } catch (err) {
          console.error(err);
          const message = getErrorMessage(err, `Failed to delete assignment.`);
          pushErrorToast({ message });
        } finally {
          actions.stopLoading(LoadingId.deleteTemplateAssignmentRequest);
        }
      },
      [actions, invalidateKpiRequests, pushErrorToast, pushSuccessToast]
    )
  );

  return { assignTemplate, deleteAssignment, updateAssignment };
}
